import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, UploadCloud, Trash2, Save } from "lucide-react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { showToast } from "../../utils/showToast";

// ─────────────────────────────────────────────────────────────────────────────
// LogoUploader — subida de logo en dos formatos: imagen (para pantalla) y su
// versión ZPL (para etiqueta térmica Zebra).
//
// Extraído de WhiteLabelSection para que lo usen tanto la empresa LOGÍSTICA
// (/admin/settings?tab=whitelabel) como el COMERCIO (/home/configuracion-empresa).
// Lo único que cambia entre ambos es la función `onUpload`; el resto —el
// pipeline de canvas, las validaciones y la UI— es idéntico.
//
// El cliente normaliza una copia a ≤320×120 px (lo que admite la etiqueta) y la
// manda aparte del original. El backend NO confía en esa normalización: revalida
// los límites sobre el ^GFA que devuelve Labelary.
// ─────────────────────────────────────────────────────────────────────────────

// Tope de la etiqueta térmica: el logo se ancla en ^FO40,22 y debe caber en
// 320×120 dots (320 px = 40 bytes/fila; 120 px sin pisar el texto en y=150).
const TARGET_W = 320;
const TARGET_H = 120;
const MAX_FILE_BYTES = 3 * 1024 * 1024; // 3 MB
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
// Umbral del preview monocromo (solo visual; Labelary aplica el suyo).
const MONO_THRESHOLD = 160;

// Carga un File en un HTMLImageElement, resolviendo cuando está decodificado.
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode_error"));
    };
    img.src = url;
  });
}

// Genera, a partir de una imagen ya cargada:
//   - zplBlob: PNG aplanado sobre blanco y escalado a ≤320×120 (entrada Labelary).
//   - monoDataUrl: preview monocromo aproximado (umbral) para mostrar al usuario.
//   - dims: dimensiones finales en px.
export function buildZplAssets(img) {
  const scale = Math.min(TARGET_W / img.width, TARGET_H / img.height, 1);
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  // Preview monocromo aproximado (umbral por luminancia).
  const imgData = ctx.getImageData(0, 0, w, h);
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    const v = lum < MONO_THRESHOLD ? 0 : 255;
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 255;
  }
  const monoCanvas = document.createElement("canvas");
  monoCanvas.width = w;
  monoCanvas.height = h;
  monoCanvas.getContext("2d").putImageData(imgData, 0, 0);
  const monoDataUrl = monoCanvas.toDataURL("image/png");

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve({ zplBlob: blob, monoDataUrl, dims: { w, h } });
      else reject(new Error("encode_error"));
    }, "image/png");
  });
}

/**
 * @param {object}   props
 * @param {string}   [props.currentLogo]  URL del logo ya guardado, si hay.
 * @param {Function} props.onUpload       async ({logoFile, zplBlob}) => respuesta
 *                                        del backend. Debe RECHAZAR ante error.
 * @param {Function} [props.onUploaded]   Callback con la respuesta, para que el
 *                                        contenedor refresque su estado.
 * @param {boolean}  [props.disabled]     Bloquea la subida (p. ej. sin empresa).
 * @param {"default"|"compact"} [props.variant]  "compact" = una sola vista
 *                                        previa y acciones en línea, para
 *                                        columnas angostas (rail de la vista
 *                                        del comercio). Admin usa "default".
 */
export default function LogoUploader({
  currentLogo,
  onUpload,
  onUploaded,
  disabled = false,
  variant = "default",
}) {
  const { t } = useTranslation();

  const [newFile, setNewFile] = useState(null);
  const [newPreviewUrl, setNewPreviewUrl] = useState("");
  const [monoPreviewUrl, setMonoPreviewUrl] = useState("");
  const [zplBlob, setZplBlob] = useState(null);
  const [dims, setDims] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Libera el object URL de la previsualización al desmontar o reemplazarlo.
  useEffect(() => {
    return () => {
      if (newPreviewUrl) URL.revokeObjectURL(newPreviewUrl);
    };
  }, [newPreviewUrl]);

  const resetSelection = () => {
    setNewFile(null);
    setNewPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setMonoPreviewUrl("");
    setZplBlob(null);
    setDims(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const processFile = async (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      showToast(t("Formato no soportado. Usa PNG, JPG o WEBP."), false);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      showToast(t("El logo supera el máximo de 3 MB."), false);
      return;
    }
    setProcessing(true);
    try {
      const { img, url } = await loadImage(file);
      const assets = await buildZplAssets(img);
      URL.revokeObjectURL(url);
      setNewPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      setNewFile(file);
      setMonoPreviewUrl(assets.monoDataUrl);
      setZplBlob(assets.zplBlob);
      setDims(assets.dims);
    } catch {
      showToast(t("No se pudo procesar la imagen."), false);
      resetSelection();
    } finally {
      setProcessing(false);
    }
  };

  const handleInputChange = (e) => processFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    processFile(e.dataTransfer?.files?.[0]);
  };

  const handleSave = async () => {
    if (!newFile || !zplBlob || disabled) return;
    setSaving(true);
    try {
      const data = (await onUpload({ logoFile: newFile, zplBlob })) || {};
      onUploaded?.(data);
      // El backend puede haber guardado la imagen sin poder generar el ZPL (si
      // el conversor no respondió). No es un error de la subida, pero el usuario
      // tiene que saber que la versión para etiquetas quedó pendiente.
      if (data.zplPending) {
        showToast(
          t("Logo actualizado. La versión para etiquetas no pudo generarse; volvé a intentarlo más tarde."),
          true,
        );
      } else {
        showToast(t("Logo actualizado"), true);
      }
      resetSelection();
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      showToast(
        typeof apiMsg === "string" ? apiMsg : t("No se pudo guardar el logo."),
        false,
      );
    } finally {
      setSaving(false);
    }
  };

  const busy = processing || saving;

  if (variant === "compact") {
    const shownLogo = newPreviewUrl || currentLogo;
    const canPick = !busy && !disabled;
    return (
      <div className="space-y-3">
        {/* La tarjeta acepta arrastrar y soltar y el clic como atajo; el control
            accesible por teclado es el botón de abajo, así no hay dos paradas
            de tabulación para la misma acción. */}
        <div
          onClick={() => canPick && !newFile && fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (canPick) setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => (canPick ? handleDrop(e) : e.preventDefault())}
          className={
            "relative h-32 w-full rounded-lg border flex items-center justify-center p-[0.75rem] overflow-hidden transition-colors " +
            (dragActive
              ? "border-2 border-dashed border-brand-500 bg-brand-50"
              : shownLogo
                ? "border-border bg-white"
                : "border-dashed border-border bg-bg-muted") +
            (canPick && !newFile ? " cursor-pointer hover:border-brand-500/60" : "") +
            (busy ? " opacity-60 cursor-wait" : "")
          }
        >
          {shownLogo ? (
            <img
              src={shownLogo}
              alt={newFile ? t("Nuevo logo") : t("Logo actual")}
              className="as-company-logo max-h-full max-w-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center gap-1.5 text-center text-fg-muted">
              <ImageIcon className="w-6 h-6 text-fg-subtle" aria-hidden="true" />
              <span className="text-xs">
                {processing ? t("Procesando…") : t("Arrastra una imagen o haz clic para subir")}
              </span>
            </div>
          )}
          {newFile && (
            <Badge tone="brand" className="absolute top-2 left-2">
              {t("Nuevo")}
            </Badge>
          )}
        </div>

        {newFile && monoPreviewUrl && (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-bg-muted p-2.5">
            <div className="shrink-0 rounded border border-border bg-white p-1">
              <img
                src={monoPreviewUrl}
                alt={t("Vista previa en etiqueta")}
                style={{ imageRendering: "pixelated" }}
                className="h-10 max-w-[7.5rem] object-contain"
              />
            </div>
            <div className="min-w-0 text-xs text-fg-muted">
              <p className="font-medium text-fg">{t("Vista previa en etiqueta (aprox.)")}</p>
              {dims && (
                <p className="tabular-nums">
                  {dims.w}×{dims.h} px{" "}
                  <span className="text-fg-subtle">
                    ({t("máx.")} {TARGET_W}×{TARGET_H})
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-fg-muted">{t("PNG, JPG o WEBP · máx. 3 MB")}</span>
          {newFile ? (
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={resetSelection} disabled={saving}>
                {t("Quitar")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={busy || disabled}
                loading={saving}
                iconLeft={<Save size={14} />}
              >
                {saving ? t("Guardando…") : t("Guardar logo")}
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canPick}
              loading={processing}
              iconLeft={<UploadCloud size={14} />}
            >
              {currentLogo ? t("Cambiar logo") : t("Subir logo")}
            </Button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* ── Logo actual ──────────────────────────────────────────────── */}
        <div>
          <label className="text-sm font-medium text-fg block mb-2">{t("Logo actual")}</label>
          <div className="aspect-[4/3] w-full rounded-xl border border-border bg-bg flex items-center justify-center p-4 overflow-hidden">
            {currentLogo ? (
              <img
                src={currentLogo}
                alt={t("Logo actual")}
                className="as-company-logo max-h-full max-w-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-fg-subtle">
                <ImageIcon className="w-8 h-8" aria-hidden="true" />
                <span className="text-xs">{t("Sin logo")}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Nuevo logo: dropzone / previsualización ───────────────────── */}
        <div>
          <label className="text-sm font-medium text-fg block mb-2">{t("Nuevo logo")}</label>

          {!newFile ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              disabled={busy || disabled}
              className={
                "aspect-[4/3] w-full rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 p-4 text-center transition-colors " +
                (dragActive
                  ? "border-brand-500 bg-brand-50"
                  : "border-border bg-bg hover:border-brand-500/50 hover:bg-bg-muted") +
                (busy ? " opacity-60 cursor-wait" : disabled ? " opacity-60 cursor-not-allowed" : " cursor-pointer")
              }
            >
              <UploadCloud className="w-7 h-7 text-fg-subtle" aria-hidden="true" />
              <span className="text-sm font-medium text-fg">
                {processing ? t("Procesando…") : t("Arrastra una imagen o haz clic para subir")}
              </span>
              <span className="text-xs text-fg-muted">{t("PNG, JPG o WEBP · máx. 3 MB")}</span>
            </button>
          ) : (
            <div className="aspect-[4/3] w-full rounded-xl border border-border bg-bg flex items-center justify-center p-4 overflow-hidden">
              <img
                src={newPreviewUrl}
                alt={t("Nuevo logo")}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleInputChange}
          />
        </div>
      </div>

      {/* ── Previsualización para etiqueta térmica (cuando hay nuevo logo) ── */}
      {newFile && (
        <div className="mt-5 rounded-xl border border-border bg-bg-muted p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <span className="text-xs font-medium text-fg-muted block mb-1.5">
                {t("Vista previa en etiqueta (aprox.)")}
              </span>
              <div className="inline-flex items-center justify-center rounded-md border border-border bg-white p-2">
                {monoPreviewUrl ? (
                  <img
                    src={monoPreviewUrl}
                    alt={t("Vista previa en etiqueta")}
                    style={{ imageRendering: "pixelated" }}
                    className="max-h-[120px] object-contain"
                  />
                ) : null}
              </div>
            </div>
            <div className="text-xs text-fg-muted space-y-1">
              <p>{t("Así se verá en blanco y negro al imprimir en la etiqueta Zebra.")}</p>
              {dims && (
                <p>
                  {t("Tamaño para etiqueta")}: <span className="font-medium text-fg">{dims.w}×{dims.h} px</span>
                  <span className="text-fg-subtle"> ({t("máx.")} {TARGET_W}×{TARGET_H})</span>
                </p>
              )}
              <p className="text-fg-subtle">
                {t("El resultado final lo genera el servidor; este preview es una aproximación.")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Acciones ─────────────────────────────────────────────────────── */}
      <div className="mt-5 flex items-center justify-end gap-2">
        {newFile && (
          <Button
            variant="outline"
            onClick={resetSelection}
            disabled={saving}
            iconLeft={<Trash2 size={16} />}
          >
            {t("Quitar")}
          </Button>
        )}
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!newFile || busy || disabled}
          loading={saving}
          iconLeft={<Save size={16} />}
        >
          {saving ? t("Guardando…") : t("Guardar logo")}
        </Button>
      </div>
    </>
  );
}
